// src/components/EquipmentVideosManagement.jsx
import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Upload,
    Select,
    Tag,
    Space,
    Card,
    Typography,
    message,
    Popconfirm,
    Row,
    Col
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    PlayCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import apiService from "../services/api";

const { Title } = Typography;
const { TextArea } = Input;

// 阻止Upload组件自动上传
const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
        onSuccess("ok");
    }, 0);
};

// 用于图片预览
const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

// 从视频文件生成第一帧缩略图
const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.setAttribute('src', URL.createObjectURL(file));
        video.crossOrigin = 'anonymous';
        video.onloadeddata = () => {
            video.currentTime = 0;
        };
        video.onseeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(video.src);
            resolve(canvas.toDataURL('image/jpeg'));
        };
        video.onerror = () => {
            resolve(null); // 生成失败则返回null
        };
    });
};


const EquipmentVideosManagement = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // 视频播放弹窗 state
    const [videoModalVisible, setVideoModalVisible] = useState(false);
    const [playingVideoUrl, setPlayingVideoUrl] = useState('');

    // 图片预览弹窗 state
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');


    useEffect(() => {
        loadVideos();
    }, []);

    // 获取文件可访问的完整URL
    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http') || path.startsWith('blob:')) return path;
        const baseUrl = apiService.baseURL.replace('/api', '');
        return path.startsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`;
    };

    const loadVideos = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const response = await apiService.equipmentVideos.getEquipmentVideoList(page, pageSize);
            if (response.code === 200) {
                setVideos(response.data.records.map(v => ({ ...v, key: v.id })));
                setPagination({
                    current: response.data.current,
                    pageSize: response.data.size,
                    total: response.data.total,
                });
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            message.error('获取列表失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (pagination) => {
        loadVideos(pagination.current, pagination.pageSize);
    };

    const beforeUpload = (file, isImage) => {
        const fileType = isImage ? 'image' : 'video';
        const maxSize = isImage ? 2 : 50;
        const isValidType = file.type.startsWith(`${fileType}/`);
        if (!isValidType) {
            message.error(`请上传${fileType}文件!`);
            return Upload.LIST_IGNORE;
        }
        const isValidSize = file.size / 1024 / 1024 < maxSize;
        if (!isValidSize) {
            message.error(`${fileType}大小不能超过 ${maxSize}MB!`);
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleSubmit = async (values) => {
        try {
            const { image, video, ...otherValues } = values;
            const formData = new FormData();

            Object.keys(otherValues).forEach(key => formData.append(key, otherValues[key]));

            if (image && image.length > 0 && image[0].originFileObj) {
                formData.append('imageFile', image[0].originFileObj);
            }

            if (video && video.length > 0 && video[0].originFileObj) {
                formData.append('videoFile', video[0].originFileObj);
            }

            let response;
            if (editingVideo) {
                formData.append('id', editingVideo.id);
                response = await apiService.equipmentVideos.update(editingVideo.id, formData);
            } else {
                response = await apiService.equipmentVideos.create(formData);
            }

            if (response.code === 200) {
                message.success(`${editingVideo ? '更新' : '创建'}成功`);
                setModalVisible(false);
                loadVideos();
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            message.error(`${editingVideo ? '更新' : '创建'}失败: ${error.message}`);
        }
    };

    const handleEdit = (video) => {
        setEditingVideo(video);
        const imageUrl = getFileUrl(video.image);
        const videoUrl = getFileUrl(video.video);

        const imageList = video.image ? [{ uid: '-1', name: 'image.png', status: 'done', url: imageUrl }] : [];
        const videoList = video.video ? [{ uid: '-2', name: 'video.mp4', status: 'done', url: videoUrl }] : [];

        form.setFieldsValue({
            ...video,
            image: imageList,
            video: videoList,
        });

        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await apiService.equipmentVideos.delete(id);
            if (response.code === 200) {
                message.success('删除成功');
                loadVideos();
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            message.error('删除失败: ' + error.message);
        }
    };

    const handleAdd = () => {
        setEditingVideo(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        setEditingVideo(null);
    };

    const handlePlayVideo = (videoUrl) => {
        if (!videoUrl) {
            message.warn('无视频文件');
            return;
        }
        setPlayingVideoUrl(getFileUrl(videoUrl));
        setVideoModalVisible(true);
    };

    const handlePreview = async (file) => {
        if (file.type?.startsWith('video/') || file.url?.includes('.mp4') || file.url?.includes('blob:')) {
            const videoUrl = file.url || URL.createObjectURL(file.originFileObj);
            handlePlayVideo(videoUrl);
            return;
        }

        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewVisible(true);
        setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
    };

    const handleVideoChange = async ({ file, fileList }) => {
        const newFileList = [...fileList];
        const currentFile = newFileList.find(f => f.uid === file.uid);

        if (currentFile && file.status === 'done' && file.originFileObj && !currentFile.thumbUrl) {
            const thumbUrl = await generateVideoThumbnail(file.originFileObj);
            if (thumbUrl) {
                currentFile.thumbUrl = thumbUrl;
                // We need to trigger a re-render of the form item with the new thumbUrl
                form.setFieldsValue({ video: newFileList });
            }
        }
    };

    const columns = [
        {
            title: '视频标题',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => (
                <Space>
                    <img src={getFileUrl(record.image)} alt={text} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} onError={(e) => { e.target.src = 'https://via.placeholder.com/50'; }} />
                    <span>{text}</span>
                </Space>
            )
        },
        { title: '设备信息', dataIndex: 'brand', key: 'brand', render: (_, r) => `${r.brand} ${r.model}` },
        { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', render: (t) => moment(t).format('YYYY-MM-DD HH:mm') },
        { title: '状态', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 1 ? 'green' : 'red'}>{s === 1 ? '已发布' : '草稿'}</Tag> },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button icon={<PlayCircleOutlined />} onClick={() => handlePlayVideo(record.video)} size="small">播放</Button>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">编辑</Button>
                    <Popconfirm title="确定删除吗？" onConfirm={() => handleDelete(record.id)}>
                        <Button danger icon={<DeleteOutlined />} size="small">删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const uploadButton = (<div><PlusOutlined /><div style={{ marginTop: 8 }}>上传</div></div>);

    return (
        <Card>
            <Title level={4} style={{ marginBottom: 16 }}>设备操作视频管理</Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16, float: 'right' }}>添加视频</Button>
            <Table dataSource={videos} columns={columns} loading={loading} pagination={pagination} onChange={handleTableChange} rowKey="id" />

            <Modal title={editingVideo ? "编辑视频" : "添加视频"} visible={modalVisible} onCancel={handleCancel} footer={null} width={800}>
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ status: 1 }}>
                    <Row gutter={16}>
                        <Col span={12}><Form.Item name="title" label="视频标题" rules={[{ required: true }]}><Input /></Form.Item></Col>
                        <Col span={12}><Form.Item name="equipmentType" label="设备类型" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}><Form.Item name="brand" label="设备品牌" rules={[{ required: true }]}><Input /></Form.Item></Col>
                        <Col span={12}><Form.Item name="model" label="设备型号" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    </Row>
                    <Form.Item name="description" label="视频描述"><TextArea rows={3} /></Form.Item>
                    <Form.Item name="status" label="状态"><Select><Select.Option value={1}>已发布</Select.Option><Select.Option value={0}>草稿</Select.Option></Select></Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="封面图片" name="image" valuePropName="fileList" getValueFromEvent={(e) => e && e.fileList}>
                                <Upload listType="picture-card" maxCount={1} customRequest={dummyRequest} beforeUpload={(file) => beforeUpload(file, true)} onPreview={handlePreview} accept="image/*">{uploadButton}</Upload>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="视频文件" name="video" valuePropName="fileList" getValueFromEvent={(e) => e && e.fileList}>
                                <Upload listType="picture-card" maxCount={1} customRequest={dummyRequest} beforeUpload={(file) => beforeUpload(file, false)} onPreview={handlePreview} onChange={handleVideoChange} accept="video/*">{uploadButton}</Upload>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item style={{ textAlign: 'right', marginTop: 24 }}>
                        <Space>
                            <Button onClick={handleCancel}>取消</Button>
                            <Button type="primary" htmlType="submit">提交</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal visible={previewVisible} title={previewTitle} footer={null} onCancel={() => setPreviewVisible(false)}>
                <img alt="预览" style={{ width: '100%' }} src={previewImage} />
            </Modal>

            <Modal title="播放视频" visible={videoModalVisible} onCancel={() => setVideoModalVisible(false)} footer={null} destroyOnClose width={720}>
                {playingVideoUrl && (
                    <video controls autoPlay style={{ width: '100%' }} key={playingVideoUrl}>
                        <source src={playingVideoUrl} type="video/mp4" />
                        浏览器不支持 video 标签。
                    </video>
                )}
            </Modal>
        </Card>
    );
};

export default EquipmentVideosManagement;
